"use client"
import { getDepictRecommendedProductsClient } from "@/actions/products/get-depict-recommended-products-client"
import { type Locale } from "@/i18n/locales"
import { type ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { transformDepictToProductData } from "@/lib/depict-client/helpers/depict-transforms"
import useDepictClientMarketAndLocaleCode from "@/lib/depict-client/helpers/useDepictClientMarketAndLocaleCode"
import { useDepictClientSessionId } from "@/lib/depict-client/helpers/useDepictClientSessionId"
import { type RecommendResponse } from "@/lib/depict-client/types/api/RecommendResponse"
import logger from "@/lib/logger/logger-init"
import { getDepictMarketCode } from "@/src/utils/depict/getDepictMarketCode"
import { getCountryCodeFromPreferredApp } from "@/src/utils/geo/getCountryCodeFromPreferredApp"
import { useEffect, useState } from "react"

type UseFetchRecommendedProductsParams = {
    productIds: string[]
    isLoading?: boolean
    limit?: number
    depictType: string
    paramsLocale: Locale
}

type UseFetchRecommendedProductsResult = {
    groupedProductsDataRecord: Record<string, ProductData[]> | null
    loading: boolean
    groupedResultCount: number
}

export const useFetchRecommendedProducts = ({
    productIds,
    isLoading = false,
    limit = 9,
    depictType,
    paramsLocale
}: UseFetchRecommendedProductsParams): UseFetchRecommendedProductsResult => {
    const [groupedProductsDataRecord, setGroupedProductsDataRecord] = useState<Record<
        string,
        ProductData[]
    > | null>(null)
    const countryCode = getCountryCodeFromPreferredApp(paramsLocale)
    const { localeCode } = useDepictClientMarketAndLocaleCode()
    const marketCode = getDepictMarketCode(countryCode)
    const [loading, setLoading] = useState(false)
    const [groupedResultCount, setGroupedResultCount] = useState(0)
    const depictSessionId = useDepictClientSessionId()

    useEffect(() => {
        const fetchRecommendedProducts = async () => {
            setLoading(true)
            const product_ids = productIds ? { product_ids: productIds } : undefined

            try {
                const request = (await getDepictRecommendedProductsClient(
                    {
                        market: marketCode,
                        locale: localeCode,
                        type: depictType,
                        limit: limit,
                        ...product_ids,
                        session_id: depictSessionId ?? undefined
                    },
                    paramsLocale
                )) as RecommendResponse

                const groupedProductsData = transformDepictToProductData(
                    request,
                    "recommendation_id"
                )

                const productDataRecord = groupedProductsData.reduce(
                    (acc, { key, items }) => {
                        acc[key] = items
                        return acc
                    },
                    {} as Record<string, ProductData[]>
                )

                setGroupedProductsDataRecord(productDataRecord)
                setGroupedResultCount(Object.keys(productDataRecord).length)
                setLoading(false)
            } catch (error) {
                logger.error("Failed to fetch recommended products")
                setLoading(false)
            }
        }

        fetchRecommendedProducts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productIds, isLoading, localeCode, marketCode])

    return {
        groupedProductsDataRecord,
        loading,
        groupedResultCount
    }
}
